import { useEffect, useRef, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import useAxios from "../../utils/useAxios";

const SEARCH_DEBOUNCE_MS = 300;

const getComponentOptionLabel = (option) => {
  const componentLabel = option?.tagNumber || option?.componentName || "";
  const parent = option?.tier_details || option?.tierDetails || option?.tier;
  const parentLabel =
    (typeof parent === "object" && (parent?.tagNumber || parent?.tierName)) ||
    option?.tierTagNumber ||
    "";

  return parentLabel && componentLabel
    ? `${parentLabel} - ${componentLabel}`
    : componentLabel;
};

const getResultsArray = (data) => {
  const normalized = Array.isArray(data) ? data : data?.results || [];
  return Array.isArray(normalized) ? normalized : [];
};

/**
 * Async, server-searched PanelComponent picker - same shape as
 * EquipmentAutocomplete/TierAutocomplete, for the "Related Components"
 * field on Instrument forms.
 *
 * @param {object} props
 * @param {string | number} props.plantId
 * @param {boolean} [props.multiple]
 * @param {object | object[] | null} [props.value]
 * @param {(value: object | object[] | null, reason: string) => void} props.onChange
 * @param {string} [props.label]
 * @param {number | string} [props.excludeId]
 * @param {boolean} [props.required]
 * @param {boolean} [props.error]
 * @param {string} [props.helperText]
 * @param {boolean} [props.disabled]
 * @param {() => void} [props.onBlur]
 */
export default function PanelComponentAutocomplete({
  plantId,
  multiple = false,
  value,
  onChange,
  label = "Panel Component",
  excludeId = null,
  required = false,
  error = false,
  helperText = "",
  disabled = false,
  onBlur,
}) {
  const api = useAxios();
  const requestIdRef = useRef(0);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !plantId) return undefined;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const controller = new AbortController();

    const searchTimer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const response = await api.get("/panel-components/", {
          params: { plant: plantId, search: search.trim() || undefined, page_size: 20 },
          signal: controller.signal,
        });
        if (requestIdRef.current === requestId) {
          const items = getResultsArray(response.data);
          setOptions(
            excludeId != null ? items.filter((item) => item.id !== excludeId) : items,
          );
        }
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        console.error("Error searching panel components:", err);
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(searchTimer);
      controller.abort();
    };
  }, [api, excludeId, open, plantId, search]);

  return (
    <Autocomplete
      multiple={multiple}
      disableCloseOnSelect={multiple}
      options={options}
      loading={loading}
      filterOptions={(opts) => opts} // server already filtered
      getOptionLabel={getComponentOptionLabel}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      value={value ?? (multiple ? [] : null)}
      onChange={(_, selected, reason) => onChange(selected, reason)}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onInputChange={(_, next) => setSearch(next)}
      onBlur={onBlur}
      disabled={disabled}
      noOptionsText={plantId ? "No matching panel components" : "Choose a plant first"}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          fullWidth
          required={required}
          error={error}
          helperText={helperText}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
